import { Card, Typography, TextField, Divider, FormGroup, MenuItem, Stack, Grid, Badge, IconButton, Tooltip, Box } from "@mui/material";
import { CRUD_GET_LIST, Refetch, useGetMainList, useGetOne } from "ra-core";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useHistory } from 'react-router-dom'
import { ChangeEvent, FC, useCallback, useMemo } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { EncryptionMappingResponseDto, CollectionSchemaResponseDto } from "@/gen/api/governance";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { BasicInfoRow, BasicInfoPiece } from "../backups/BackupBasicInfoCard";
import MappedIcon from "@/icons/Mapped";
import SkippedIcon from "@/icons/Skipped";
import PendingReviewIcon from "@/icons/PendingReview";


interface useCollectionSelectionCardProp {
  refetch: Refetch;
  CollectionSelectionCard: FC<{
      collection: CollectionSchemaResponseDto | undefined;
      setCollection: Dispatch<SetStateAction<CollectionSchemaResponseDto | undefined>>;
  }>;
}

export const useCollectionSelectionCard = (record: EncryptionMappingResponseDto|undefined, useSource: 'pending-collections'|'collections'): useCollectionSelectionCardProp => {
  const { data: dbCollectionsMap, refetch } = 
    useGetMainList<CollectionSchemaResponseDto & RecPatch>(!(record?.databaseId) ? 'noop' : useSource, pages(), sorts(), { dbId: record?.databaseId }, {action: CRUD_GET_LIST,})
  const dbCollections = useMemo(() => 
    Object.values(dbCollectionsMap || {})
      .sort((a, b) => ((a.name || '') < (b.name || '') ? -1 : 1)), [dbCollectionsMap])

  const CollectionSelectionCard : FC<{ 
    collection: CollectionSchemaResponseDto | undefined, 
    setCollection: Dispatch<SetStateAction<CollectionSchemaResponseDto | undefined>>,
  }> = ({ collection, setCollection }) => {
  
    
    const mappingAmend = { skip: 0, mapped: 0, 'pending-review': 0 }
    const { data: fieldsCountsData } = useGetOne<FieldsCounts & RecPatch>(collection ? 'db-fields-mapping-count' : 'noop', collection?.databaseId || -1)
    const fieldsCounts: FieldsCounts = fieldsCountsData || {}
    
    const handleCollectionChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      const selectedCollection = dbCollections.find(x => x.collectionId === +e.target.value)
      setCollection(selectedCollection)
    }, [dbCollections])
    
    const history = useHistory()
    useEffect(() => {
      if (!collection && dbCollections.length > 0) {
        setCollection(dbCollections[0])
      }
      if (collection) {
        if (dbCollections.length > 0) {
          const collection_v2 = dbCollections.find(d => d.id === collection.collectionId)
          if (collection_v2) {
            setCollection(v => v?.jsonSchema !== collection_v2.jsonSchema ? collection_v2 : v)
          } else {
            setCollection(dbCollections[0])
          }
        } 
        if (dbCollections.length === 0) {
          history.goBack()
        }
      }
    }, [collection, setCollection, dbCollections.length, history])
  
    return useMemo(() => (
      <Card>
        <BasicInfoRow>
          <BasicInfoPiece>
            <Typography
              color="textPrimary"
              variant="h6"
            >
              Database
            </Typography>
            <Typography
              color="textSecondary"
              variant="body1"
            >
              {record?.databaseName}
            </Typography>
          </BasicInfoPiece>
          <BasicInfoPiece>
            <Typography
              color="textPrimary"
              variant="h6"
            >
              Engine
            </Typography>
            <Typography
              color="textSecondary"
              variant="body1"
            >
              {record?.engineVersion + ' ' + (record?.engineEdition || '')}
            </Typography>
          </BasicInfoPiece>
        </BasicInfoRow>
        <Divider />
        <Grid
          container
          sx={{
            px: 3,
            py: 1,
            alignItems: 'center',
          }}
        >
          <Grid item xs={8}>
            <Grid container>
              <Grid item xs={3}>            
                <Typography
                  id="Collection-name"
                  color="textPrimary"
                  variant="h6"
                  sx={{ flex: 1 }}
                >
                  Collection Name
                </Typography>
              </Grid>            
              <Grid item xs={9} sx={listSx}>
                <FormGroup>
                  <TextField
                    value={collection?.collectionId || dbCollections[0]?.collectionId || ''}
                    onChange={handleCollectionChange}
                    select
                    size="small"
                    id='Collection-select'
                    SelectProps={{
                      inputProps: {
                        labelId: 'Collection-name',
                      },
                      MenuProps: { PaperProps: { sx: listSx } },
                    }}
                  >
                    {dbCollections.map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        <Box className="gcontainer"
                          sx={{ 
                            width: '100%',
                            '& span.MuiTypography-body2': { 
                              maxWidth: 320,
                              wordBreak: 'keep-all',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mr: 'auto',
                            }
                          }}
                        >
                          <Typography component="span" variant="body2" 
                            className="i2"                        
                          >
                            {t.name}
                          </Typography>
                          <Typography component="span" variant="caption" color="success.main"
                            className="i3"
                          >
                            <Badge 
                              color={"success" as any} 
                              badgeContent={t.mappedCount} 
                              // showZero 
                              max={9999} 
                            />
                          </Typography>
                          <Typography component="span" variant="caption"
                            className="i4"
                            sx={{ color: 'warning.light', mr: 2 }}
                          >
                            <Badge 
                              color={"warning" as any} 
                              badgeContent={t.id === collection?.collectionId 
                                ? ((t.pendingReviewCount || 0) + mappingAmend["pending-review"]) 
                                : (t.pendingReviewCount || 0)} 
                              // showZero 
                              max={9999} 
                              sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                            />
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </FormGroup>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex' }}>
            <Stack 
              direction="row" 
              spacing={4} 
              sx={{ 
                mx: 'auto',
                '& span.MuiBadge-badge': { whiteSpace: 'nowrap' },
              }} 
            >
              <Tooltip title="Mapped">
                <IconButton color={"inherit" as any} size="small">
                  <Badge 
                    color={"success" as any} 
                    badgeContent={'' + 
                      (fieldsCounts.mappedCount || 0) + 
                      (mappingAmend.mapped !== 0 
                        ? ' ' + toSignedString(mappingAmend.mapped)
                        : '')}
                    showZero 
                    max={9999}
                  >
                    <MappedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
  
              <Tooltip title="Skipped">
                <IconButton color={"inherit"} size="small">
                  <Badge 
                    color="secondary" 
                    badgeContent={'' + 
                      (fieldsCounts.skippedCount || 0) + 
                      (mappingAmend.skip !== 0 
                        ? ' ' + toSignedString(mappingAmend.skip)
                        : '')}
                    showZero 
                    max={9999}
                  >
                    <SkippedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
  
              <Tooltip title="Pending Review">
                <IconButton color={"inherit" as any} size="small">
                  <Badge 
                    color={"warning" as any} 
                    badgeContent={'' + 
                      (fieldsCounts.pendingReviewCount || 0) + 
                      (mappingAmend["pending-review"] !== 0 
                        ? ' ' + toSignedString(mappingAmend["pending-review"])
                        : '')}
                    showZero 
                    max={9999}
                    sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                  >
                    <PendingReviewIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Card>
    ), [record, collection, dbCollections, fieldsCountsData, mappingAmend["mapped"], mappingAmend["skip"], mappingAmend["pending-review"], ])
  }

  return { refetch, CollectionSelectionCard }
}

const listSx = {
  '& .gcontainer': {                          
    display: 'grid',
    gridAutoColumns: '1fr minmax(20px, auto) minmax(20px, auto)',
    columnGap: 0.5,
    '& .i2': { gridColumn: '1 / span 1' },
    '& .i3': { gridColumn: '2 / span 1' },
    '& .i4': { gridColumn: '3 / span 1' }
  }
}

const toSignedString = (x:number) => (Math.abs(x) === x ? '+' : '-') + Math.abs(x)

interface FieldsCounts {
  mappedCount?: number;
  skippedCount?: number;
  pendingReviewCount?: number;
}
